import { useQuery, useReactiveVar } from "@apollo/client";
import {
  Button,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { IoReaderOutline, IoAnalytics } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { GET_REVIEWED_URL } from "../../../graphql/operations/campaignOperations/operations";
import { BiPencil } from "react-icons/bi";
import { userDetailsVar } from "../../../lib/cache";
import { useHitlFilters } from "..";
import { GET_URL_CLASSIFICATION } from "../../../graphqlExternal/operations/operations";
import CustomBox from "../../../components/segmentCard/CustomBox";
import { LuLink } from "react-icons/lu";
import { encodeURL } from "../../../utils/encodeUrl";
import { ReviewTypeEnum } from "../Header";
import { hitlPredictionsAccess } from "../../../utils/accessControls";

interface UrlCardProps {
  url: string;
  urlIndex: number;
}

export const UrlCard = ({ url, urlIndex }: UrlCardProps) => {
  const hitlFilters = useHitlFilters();
  const navigate = useNavigate();
  const currentUser = useReactiveVar(userDetailsVar);

  let isCorrectionButtonDisabled = false;
  let isReviewButtonDisabled = false;
  let isPredictionsDisabled = false;

  const { data, loading, error } = useQuery(GET_REVIEWED_URL, {
    skip: !url,
    variables: {
      url: url,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    data: reviewData,
    loading: reviewLoading,
    // error: reviewError,
  } = useQuery(GET_URL_CLASSIFICATION, {
    skip: !url,
    context: { clientName: "illumaDbEndpoint", type: "graphql" },
    variables: {
      publisher: hitlFilters.publisherFilter.value,
      url: url,
    },
    fetchPolicy: "cache-and-network",
  });

  // const {
  //   loading: predictiveLoading,
  //   error: predictiveError,
  //   data: predictiveData,
  // } = useQuery(GET_PREDICTIVE_CORRECTIONS, {
  //   variables: {
  //     predictedFromUrl: url,
  //   },
  //   fetchPolicy: "cache-and-network",
  // });

  if (loading || reviewLoading || (!data && !reviewData)) {
    return <></>;
  }

  if (error || !data || data === null) {
    isCorrectionButtonDisabled = true;
    isPredictionsDisabled = true;
  }

  // if (
  //   reviewError ||
  //   !reviewData ||
  //   reviewData.urlClassification === null ||
  //   reviewData.urlClassification.pageUrl === null
  // ) {
  //   isReviewButtonDisabled = true;
  // }

  // Disable review button if url doesnt exist in publisher db
  if (
    (!reviewData ||
      reviewData === undefined ||
      reviewData?.urlClassification === undefined ||
      !reviewData?.urlClassification) &&
    (!data?.reviewedUrl?.originalClassification ||
      data?.reviewedUrl?.originalClassification === null)
  ) {
    isReviewButtonDisabled = true;
  }

  // if (
  //   predictiveError ||
  //   !predictiveData ||
  //   predictiveData.predictiveCorrections.length === 0
  // ) {
  //   isPredictionsDisabled = true;
  // }

  return (
    <CustomBox variant="squareWhite">
      {/* id={`url-card-${urlIndex}`}
      bg={"white"}
      // borderRadius={"lg"}
      px={[2, null, null, 6, null]}
      py={[2, null, null, 4, null]}
      transition={"all 0.3s ease-in-out"}
      overflow={"hidden"} // ToDo: Remove this
      my={4} */}

      <Grid
        templateColumns={"repeat(8, 1fr)"}
        w={"100%"}
        // onClick={() => {
        // hitlFilters.setSelectedUrl(url);
        // hitlFilters.setSelectedIndex(urlIndex);
        // }}
        _hover={{
          cursor: isReviewButtonDisabled ? "not-allowed" : "pointer",
        }}
      >
        <GridItem
          colSpan={7}
          maxW={"100%"}
          px={2}
          style={{
            pointerEvents: isReviewButtonDisabled ? "none" : "auto",
          }}
          onClick={() => {
            hitlFilters.setUrlIndex(urlIndex);
            navigate(`../${encodeURL(url)}/review`);
          }}
        >
          <Text
            fontWeight={"medium"}
            fontSize={["md", null, null, "md", null]}
            color={isReviewButtonDisabled ? "inactiveButton" : "text.url"}
            // isTruncated
            variant={"unStyled"}
            _hover={{
              color: "primary.dark",
            }}
            _active={{
              color: "primary.darker",
            }}
            py={2}
            sx={{
              overflow: "hidden",
              display: "flex",
              span: {
                color: isReviewButtonDisabled ? "inactiveButton" : "text.light",
                fontSize: ["md", null, null, "md", null],
                verticalAlign: "middle",
                mr: 2,
              },
            }}
          >
            <span>{<LuLink />}</span>
            {url}
          </Text>
          {hitlFilters.correctionsFilter.value === ReviewTypeEnum.All &&
            !isReviewButtonDisabled &&
            reviewData?.urlClassification &&
            reviewData.urlClassification.lastUpdateTime !== null &&
            (() => {
              let updateTime: Date;

              const lastUpdateTime =
                reviewData?.urlClassification?.lastUpdateTime;
              if (lastUpdateTime) {
                updateTime = new Date(lastUpdateTime);
                if (isNaN(updateTime.getTime())) {
                  updateTime = new Date("");
                }
              } else {
                updateTime = new Date("");
              }
              return (
                <HStack spacing={2} gridRow={1}>
                  <Text color="muted" fontSize="sm">
                    <span>Last update time: </span>
                    {formattedTime(updateTime)}
                  </Text>
                </HStack>
              );
            })()}
          {hitlFilters.correctionsFilter.value === ReviewTypeEnum.Corrections &&
            !isCorrectionButtonDisabled &&
            data.reviewedUrl.createdAt !== null &&
            (() => {
              let updateTime;
              try {
                updateTime = new Date(data.reviewedUrl.createdAt);
              } catch {
                updateTime = new Date("");
              }
              return (
                <HStack spacing={2} gridRow={1}>
                  <Text color="muted" fontSize="sm">
                    <span>Corrected at: </span>
                    {formattedTime(updateTime)}
                  </Text>
                </HStack>
              );
            })()}
        </GridItem>
        <GridItem colSpan={1}>
          <HStack align={"center"} justify={"flex-end"} height={"100%"}>
            <Link to={`../${encodeURL(url)}/review`}>
              <IconButton
                aria-label={"link to review"}
                icon={<BiPencil />}
                variant={"unstyled"}
                display={["flex", null, "none", null, null]}
                isDisabled={isReviewButtonDisabled}
                isLoading={loading}
              />
              <Button
                fontWeight={"semibold"}
                variant={"unStyled"}
                fontSize={["sm", null, null, "md", null]}
                display={["none", null, "flex", null, null]}
                isDisabled={isReviewButtonDisabled}
                onClick={() => {
                  hitlFilters.setUrlIndex(urlIndex);
                }}
                leftIcon={
                  <Icon
                    as={BiPencil}
                    w={[4, null, null, 5, null]}
                    h={[4, null, null, 5, null]}
                  />
                }
              >
                Review
              </Button>
            </Link>
            <Link to={`../${encodeURL(url)}/past-review`}>
              <IconButton
                aria-label={"link to correction"}
                icon={<IoReaderOutline />}
                variant={"unStyled"}
                display={["flex", null, "none", null, null]}
                isDisabled={isCorrectionButtonDisabled}
                isLoading={loading}
              />
              <Button
                fontWeight={"semibold"}
                variant={"unStyled"}
                fontSize={["sm", null, null, "md", null]}
                display={["none", null, "flex", null, null]}
                onClick={() => {
                  hitlFilters.setUrlIndex(urlIndex);
                }}
                leftIcon={
                  <Icon
                    as={IoReaderOutline}
                    w={[4, null, null, 5, null]}
                    h={[4, null, null, 5, null]}
                  />
                }
                isDisabled={isCorrectionButtonDisabled}
                isLoading={loading}
                // hidden={!currentUser?.isStaff}
              >
                Correction
              </Button>
            </Link>
            {currentUser &&
              (currentUser.isStaff ||
                hitlPredictionsAccess.includes(
                  currentUser?.agency.partner
                )) && (
                <Link to={`../${encodeURL(url)}/predictive-correction`}>
                  <IconButton
                    aria-label={"link to predictive-correction"}
                    icon={<IoAnalytics />}
                    variant={"unStyled"}
                    display={["flex", null, "none", null, null]}
                    isDisabled={isPredictionsDisabled}
                    isLoading={loading}
                  />
                  <Button
                    fontWeight={"semibold"}
                    variant={"unStyled"}
                    fontSize={["sm", null, null, "md", null]}
                    display={["none", null, "flex", null, null]}
                    onClick={() => {
                      hitlFilters.setUrlIndex(urlIndex);
                    }}
                    leftIcon={
                      <Icon
                        as={IoAnalytics}
                        w={[4, null, null, 5, null]}
                        h={[4, null, null, 5, null]}
                      />
                    }
                    isDisabled={isPredictionsDisabled}
                    isLoading={loading}
                  >
                    Predictions
                  </Button>
                </Link>
              )}
          </HStack>
        </GridItem>
      </Grid>
    </CustomBox>
  );
};

const formattedTime = (date: Date | null) => {
  if (!date || isNaN(date.getTime())) {
    return ""; // Fallback message for invalid or null dates
  }
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};
